import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { idCardService } from '../../services/idCardService';
import type { IdCardAudience, IdCardContext } from '../../types/idCards';
import { useBranding } from '../../utils/pdf';
import type { CardSchool } from './render/faces/shared';

/** A date as yyyy-mm-dd, for date inputs. */
export const isoDay = (d: Date): string => d.toISOString().slice(0, 10);

/**
 * What every card needs from the school: its name, logo (ready to draw),
 * address and phone, and the active session — a student card is valid
 * until the session ends; a staff card for a year.
 */
export const useCardContext = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [context, setContext] = useState<IdCardContext | null>(null);

  useEffect(() => {
    let cancelled = false;
    idCardService.context()
      .then(c => { if (!cancelled) setContext(c); })
      .catch((err: any) => { if (!cancelled) enqueueSnackbar(err?.message || 'Could not load the school details for the cards', { variant: 'error' }); });
    return () => { cancelled = true; };
  }, [enqueueSnackbar]);

  const { branding, ready: logoReady } = useBranding(context?.school.name, context?.school.logo);
  const school = useMemo<CardSchool | null>(() => (context ? {
    name: context.school.name,
    logo: branding.logoDataUrl ?? null,
    address: context.school.address,
    phone: context.school.phone,
  } : null), [context, branding.logoDataUrl]);

  const sessionEnd = context?.activeSession?.endDate;
  const defaultValidUntil = useCallback((audience: IdCardAudience): string => {
    if (audience === 'student' && sessionEnd) return sessionEnd.slice(0, 10);
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return isoDay(d);
  }, [sessionEnd]);

  return { school, activeSession: context?.activeSession ?? null, ready: !!school && logoReady, defaultValidUntil };
};
